import { type FC, useMemo } from 'react'
import { Chip } from '@/components/commons/chip'
import { PlusIcon } from '@/components/ui/icons'
import { EditingQaChip } from '@/features/photoset/components/editing-qa-chip'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import {
	type Flag,
	type Image,
	type PhotosetSpace,
	StepKey
} from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface EditingQaFlagsSelectorProps {
	image: Omit<Image, 'space'>
	types?: Flag['type'][]
	photosetSpaceId: PhotosetSpace['id']
	isQaView: boolean
}

export const EditingQaFlagsSelector: FC<EditingQaFlagsSelectorProps> = ({
	image,
	types,
	photosetSpaceId,
	isQaView
}) => {
	const { t } = useTranslation()

	const flags = useMemo(() => {
		if (!image.flags) {
			return []
		}
		if (!types) {
			return image.flags
		}

		return image.flags.filter((flag) => types.includes(flag.type))
	}, [image.flags, types])

	return (
		<div className='flex w-full flex-wrap gap-1.5'>
			{flags.map((flag) => (
				<EditingQaChip
					flag={flag}
					image={image}
					isQaView={isQaView}
					key={flag.id}
					label={t(`photoset:flags.${flag.key_name}.label`)}
					photosetSpaceIds={[photosetSpaceId]}
				/>
			))}

			{isQaView && (
				<FlagsPopover
					disableScrollBelow
					photosetSpaceIds={[photosetSpaceId]}
					selectedImagesIds={[image.id]}
					stepId={StepKey['editing-qa-flags']}
					trigger={
						<Chip
							endSlot={<PlusIcon />}
							isActive={false}
							label={t('common:flag', { count: 1 })}
							variant='info'
						/>
					}
					types={types}
				/>
			)}
		</div>
	)
}
